import { Injectable } from '@angular/core';

import { BehaviorSubject, debounceTime, finalize, Observable, Subject, Subscription } from 'rxjs';

import { BaseLogging } from '@base/base-logging';
import { DEFAULT_MAIN_MENU, FeedbackState, getUuid, MainMenu, MainMenuItem, ObjectType, PREDEFINED_MAIN_MENU_ITEMS, PrefsState, SM, Tag, traverseTreeArray, VzLocalPrefs } from '@models';
import { StoreService } from './store.service';
import { AuthService } from './auth.service';
import { IObjectEvent, StompService } from './stomp.service';

const KEY_MENU = 'menu';
const KEY_PREFS = 'prefs';
const KEY_FEEDBACK = 'feedback';
const KEY_LOCAL_PREFS = 'localprefs';

@Injectable({ providedIn: 'root' })
@Tag('PrefsService')
export class PrefsService extends BaseLogging {

    private _loggedIn: boolean = false;
    private _prefsDebouncer: Subject<PrefsState> = new Subject();
    private _prefs?: PrefsState;
    private _fbDebouncer: Subject<FeedbackState> = new Subject();
    private _fb?: FeedbackState;
    private _ref: any;
    private _gettingMenu?: Subscription;

    menu!: BehaviorSubject<MainMenu>;
    requestMenu: Subject<void> = new Subject();
    localPrefs!: BehaviorSubject<VzLocalPrefs>;

    constructor(
        protected _store: StoreService,
        private _auth: AuthService,
        private _stomp: StompService
    ) {
        super();
        this._L('constructor');
        let menu = this._store.getItem(KEY_MENU, { items: DEFAULT_MAIN_MENU });
        if (!menu || !menu.items || menu.items.length < 5) {
            menu = DEFAULT_MAIN_MENU;
        }
        this.menu = new BehaviorSubject(new MainMenu(menu));
        this.localPrefs = new BehaviorSubject(new VzLocalPrefs(this._store.getItem(KEY_LOCAL_PREFS)));
        const us = this._store.getState('user');
        this._loggedIn = us.isLoggedIn() && !us.isGuest;
        this.loadStoredPrefs();
        this._store.state('user').subscribe(st => {
            const loggedIn = st.isLoggedIn() && !st.isGuest;
            if (this._loggedIn != loggedIn) {
                this._loggedIn = loggedIn;
                // if (loggedIn) {
                //     this.loadStoredPrefs();
                // }
            }
        });
        this._store.state('prefs').subscribe(st => {
            // this._W('constructor', 'state.prefs new state:', st);
            this._prefsDebouncer.next(st);
        });
        this._prefsDebouncer.pipe(debounceTime(500)).subscribe(st => this.storePrefs(st));
        this._store.state('feedback').subscribe(st => this._fbDebouncer.next(st));
        this._fbDebouncer.pipe(debounceTime(500)).subscribe(st => this.storeFeedback(st));
        // this._stomp.onObject.subscribe(e => this._handleObject(e));
        this._stomp.onObjectEvent.subscribe(e => this._handleObjectEvent(e));
        this.requestMenu.pipe(debounceTime(500)).subscribe(() => this.getMenu());
    }

    storePrefs(st: PrefsState): void {
        // this._L('storePrefs', '_loggedIn:', this._loggedIn, '_prefs:', !!this._prefs);
        if (this._loggedIn && this._prefs) {
            // this._L('storePrefs', '_ref:', this._ref, 'st._ref:', st._ref);
            if (!this._ref || st._ref != this._ref) {
                st._dt = new Date();
                this._prefs = st;
                // this._L('storePrefs', 'send new prefs to backend:', this._prefs);
                this._auth.setPref(KEY_PREFS, st).subscribe({ error: err => this._W('storePrefs', `Error saving pref "${KEY_PREFS}":`, err) });
            }
            else {
                // this._L('storePrefs', 'clear _ref');
                this._ref = undefined;
            }
        }
    }

    loadStoredPrefs(cb?: () => void): void {
        if (this._loggedIn) {
            // this._W('loadStoredPrefs');
            this.getMenu();
            this._auth.getPref<PrefsState>(KEY_PREFS).pipe(finalize(() => cb?.())).subscribe({
                next: st => {
                    // const stOld = this._store.getStateDirect('prefs');
                    if (!st) {
                        // this._L('loadStoredPrefs', 'no saved prefs found on server, create default');
                        this._prefs = new PrefsState({ _dt: new Date() });
                        this._store.setState('prefs', this._prefs);
                    }
                    else { // if (!st._dt) {
                        // this._L('loadStoredPrefs', 'saved prefs has no date, set current and migrate');
                        // this._L('loadStoredPrefs', 'got prefs from server:', st);
                        // st._dt = new Date();
                        this._prefs = this._store.migrateState('prefs', st);
                        this._ref = getUuid();
                        this._prefs._ref = this._ref;
                        this._store.setState('prefs', this._prefs);
                    }
                    // else if (!stOld._dt || stOld._dt < st._dt) {
                    //     this._L('loadStoredPrefs', 'saved prefs has no date or date is older, override current prefs with loaded from backend');
                    //     this._prefs = st;
                    //     this._store.setState('prefs', this._store.migrateState('prefs', st));
                    // }
                    // else {
                    //     this._L('loadStoredPrefs', 'saved prefs has newer date, skip', stOld._dt, '>', st._dt);
                    //     this._prefs = stOld;
                    // }
                    // this._L('loadStoredPrefs', '_prefs:', this._prefs);
                },
                error: err => this._W('loadStoredPrefs', `Error restoring pref "${KEY_PREFS}":`, err)
            });
            this._auth.getPref<FeedbackState>(KEY_FEEDBACK).subscribe({
                next: st => {
                    const stOld = this._store.getStateDirect('feedback');
                    if (!st) {
                        // this._L('loadStoredPrefs', 'no saved feedback found on server, create default');
                        this._fb = new FeedbackState({ _dt: new Date(0) });
                        this._store.setState('feedback', this._fb);
                    }
                    else if (!st._dt) {
                        // this._L('loadStoredPrefs', 'saved feedback has no date, set current and migrate');
                        st._dt = new Date();
                        this._fb = st;
                        this._store.setState('feedback', this._store.migrateState('feedback', st));
                    }
                    else if (!stOld._dt || stOld._dt < st._dt) {
                        // this._L('loadStoredPrefs', 'saved feedback has no date or date is older, override current feedback with loaded from backend');
                        this._fb = st;
                        this._store.setState('feedback', this._store.migrateState('feedback', st));
                    }
                    else {
                        // this._L('loadStoredPrefs', 'saved feedback has newer date, skip', stOld._dt, '>', st._dt);
                        this._fb = stOld;
                    }
                    // this._L('loadStoredPrefs', '_fb:', this._fb);
                },
                error: err => this._W('loadStoredPrefs', `Error restoring pref "${KEY_FEEDBACK}":`, err)
            });
        }
    }

    storeFeedback(st: FeedbackState): void {
        // this._L('storeFeedback', '_loggedIn:', this._loggedIn, '_fb:', !!this._fb);
        if (this._loggedIn && this._fb) {
            st._dt = new Date();
            this._fb = st;
            this._auth.setPref(KEY_FEEDBACK, st).subscribe({ error: err => this._W('storeFeedback', `Error saving pref "${KEY_FEEDBACK}":`, err) });
        }
    }

    // private _handleObject(obj: BaseSystemModel | OLD_BaseSystemModel): void {
    // }

    private _handleObjectEvent(e: IObjectEvent): void {
        if (e && e.type == ObjectType.Prefs) {
            if (e.ids?.indexOf('menu') != -1) {
                // console.log('=[PrefsService]===> _handleObjectEvent[pref/menu]: ', e);
                this.getMenu();
            }
        }
    }

    getMenu(): void {
        // console.log('=[PrefsService]===> getMenu, _gettingMenu: ', this._gettingMenu);
        if (!this._gettingMenu) {
            // console.log('=[PrefsService]===> getMenu, sending _auth.getPref("menu")...');
            this._gettingMenu = this._auth.getPref('menu').subscribe({
                next: mj => {
                    const menu = new MainMenu(mj);
                    // console.log('=[PrefsService]===> getMenu, _auth.getPref, got menu:', mj);
                    const lm = this.menu.getValue();
                    if (menu && menu.items && menu.items.length > 5 && (!lm || !lm.modified || (menu.modified && menu.modified > lm.modified))) {
                        traverseTreeArray(menu.items, 'children', mi => {
                            if (mi.id && PREDEFINED_MAIN_MENU_ITEMS[mi.id] && PREDEFINED_MAIN_MENU_ITEMS[mi.id].unique) {
                                mi.parse(PREDEFINED_MAIN_MENU_ITEMS[mi.id]);
                            }
                        });
                        // console.log('=[PrefsService]===> getMenu, _auth.getPref, setting new menu:', menu);
                        if (!mj.ver || mj.ver < menu.ver) {
                            if (MainMenu.VER == 1) {
                                if (!traverseTreeArray(menu.items, 'children', mi => mi.id == 'wiki')) {
                                    menu.items.unshift(new MainMenuItem(PREDEFINED_MAIN_MENU_ITEMS.wiki));
                                    this._auth.setPref('menu', menu).subscribe();
                                }
                            }
                        }
                        this.menu.next(menu);
                    }
                    else {
                        // console.log('=[PrefsService]===> getMenu, _auth.getPref skipping...');
                    }
                    this._gettingMenu = undefined;
                },
                error: () => this._gettingMenu = undefined
            });
        }
    }

    saveMenu(menu: MainMenu): Observable<void> {
        menu.modified = new Date();
        // console.log('=[PrefsService]===> saveMenu: ', new MainMenu(menu));
        this.menu.next(menu);
        return this._auth.setPref('menu', menu);
    }

    patchLocalPrefs(patch: SM<any>): void {
        // console.warn('=[PrefsService]===> patchLocalPrefs: ', patch);
        const lp = this.localPrefs.getValue().parse(patch);
        this._store.setItem(KEY_LOCAL_PREFS, lp);
        this.localPrefs.next(lp);
    }

}
